<template lang="pug">
div
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		template(slot="airline-prices")
			v-divider.my-3
			airline-prices(:airports="resort.departures.airports")

		template(slot="airport-map")
			client-only
				mapbox.mt-6(
					ref="map"
					style="height:600px"
					:layers="layers"
					:sources="sources"
					:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
					@map-styleload="mapStyle")
					div(slot="popup")
						span.h3 {{popupData.name}}
</template>

<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import { ref } from '@vue/composition-api';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import areasReachability from '@/mixins/areas-reachability.js';
import useMap from '@/use/map';
import AirlinePrices from '@/components/airports/airline-prices';
import { interpolateFields } from '@/utils/interpolate.js';

const getResortAirport = gql`
	query getResortAirport($slug: String!, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		resort(slug: $slug) {
			id
			name
			slug
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			area {
				id
				name
				slug
			}
			geography {
				location {
					type
					coordinates
				}
			}
			departures: departures {
				airports(limit: 5) {
					distance
					minutes
					node {
						id
						name
						slug
						flights
						geography {
							location {
								type
								coordinates
							}
						}
						airlines {
							url
							price
							airline {
								id
								name
							}
						}
					}
				}
			}
		}
	}
	${Content.fragments}
	${MetaFragment}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/dorpen/:resort/vliegveld',
			'de-DE': '/skiorte/:resort/flughafen',
			'nl-BE': '/dorpen/:resort/vliegveld'
		}
	},
	name: 'ResortAirport',
	components: {
		AirlinePrices,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	mixins: [areasReachability],
	layout: 'default',
	setup() {
		const combinedLocations = ref([]);
		const { markers, sources, attachListeners, popupData } =
			useMap(combinedLocations);

		const layers = [
			{
				id: 'markers',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.4,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			combinedLocations,
			sources,
			layers,
			markers,
			attachListeners,
			popupData
		};
	},
	// eslint-disable-next-line @milqmedia/milq/page-has-meta
	asyncData({ params, app, route, redirect }) {
		return app.$graphClient
			.query({
				query: getResortAirport,
				variables: {
					slug: params.resort,
					path: route.path,
					fallback: route.path.replace(params.resort, '_resort')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(
						404,
						null,
						'resorts',
						'resorts-resort-airport-01',
						errors
					);
				}
				if (errors) {
					throw errors[0];
				}
				if (!data.resort.departures.airports.length) {
					return redirect(
						app.localePath({
							name: 'resorts-resort-reachability',
							params: { resort: data.resort.slug }
						})
					);
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				const hours = Math.trunc(
					parseInt(data.resort.departures.airports[0].minutes) / 60
				);
				const minutes =
					parseInt(data.resort.departures.airports[0].minutes) % 60;

				const time =
					hours.toString().padStart(2, 0) +
					':' +
					minutes.toString().padStart(2, 0);

				const interpolater = interpolateFields({
					find: /{(airport|time)}/gi,
					replace: (match, group) => {
						return {
							airport: data.resort.departures.airports[0].node.name,
							time
						}[group];
					}
				});
				interpolater(data.contentpage.meta, ['title', 'description']);
				interpolater(data.contentpage.content, ['markdown']);
				interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Village: data.resort.id,
							Skiresort: data.resort.area.id,
							State: data.resort.region.id,
							Country: data.resort.country.id
						}
					}
				});
				app.resortBreadcrumbs(data.resort, {
					[app.i18n.t('page.resorts._resort.airport:breadcrumb')]: {
						name: 'resorts-resort-airport',
						params: { resort: data.resort.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'resorts-resort-airport-02', error);
			});
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	mounted() {
		this.combinedLocations = [
			{
				__typename: 'Resort',
				geography: this.resort.geography,
				id: this.resort.id,
				name: this.resort.name,
				slug: this.resort.slug
			},
			...this.resort.departures.airports.map(airport => {
				const airportNode = this.$lodash.cloneDeep(airport.node);
				return airportNode;
			})
		];
		if (this.$refs.map) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}
	},
	created() {
		this.$emit('update:showSidebar', false);
	},
	methods: {
		mouseClick(feature) {
			switch (feature.properties.__typename) {
				case 'Resort':
					this.$router.push(
						this.localePath({
							name: 'resorts-resort',
							params: { resort: feature.properties.slug }
						})
					);
					break;
				case 'Airport':
					this.$router.push(
						this.localePath({
							name: 'airports-airport',
							params: { airport: feature.properties.slug }
						})
					);
					break;
			}
		},
		mapStyle(map) {
			this.$refs.map.fitToCurrentBounds();
			this.attachListeners(
				map,
				this.$refs.map,
				'markers',
				'geojson-markers',
				this.mouseClick
			);
		}
	}
};
</script>
